/**
 * Client helpers for native auth (password + OTP + JWT).
 * Blueprint: docs/NATIVE_AUTH.md
 */

const ACCESS_KEY = 'construx-auth-access';
const REFRESH_KEY = 'construx-auth-refresh';
const EMAIL_KEY = 'construx-auth-email';
const EXPIRES_KEY = 'construx-auth-expires';

export function normalizeEmail(raw) {
  return String(raw || '')
    .trim()
    .toLowerCase();
}

/** Format check only — server decides allowlist / domain. */
export function isValidEmailFormat(email) {
  const normalized = normalizeEmail(email);
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
}

/** @deprecated Use isValidEmailFormat; server enforces allowlist. */
export function isAllowedDomainEmail(email) {
  return isValidEmailFormat(email);
}

export function readStoredSession() {
  try {
    const accessToken = localStorage.getItem(ACCESS_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const email = localStorage.getItem(EMAIL_KEY);
    const expiresAt = localStorage.getItem(EXPIRES_KEY);
    if (!accessToken || !email || !expiresAt) return null;
    return { accessToken, refreshToken, email, expiresAt };
  } catch {
    return null;
  }
}

export function writeStoredSession({ accessToken, refreshToken, email, expiresAt }) {
  localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(EMAIL_KEY, normalizeEmail(email));
  localStorage.setItem(EXPIRES_KEY, expiresAt);
}

export function clearStoredSession() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    localStorage.removeItem('construx-domain-auth-token');
    localStorage.removeItem('construx-domain-auth-email');
    localStorage.removeItem('construx-domain-auth-expires');
  } catch {
    /* ignore */
  }
}

async function postJson(url, body, { method = 'POST', token } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, {
    method,
    headers,
    body: method === 'GET' ? undefined : JSON.stringify(body || {}),
  });
  const contentType = res.headers.get('content-type') || '';
  let data = {};
  if (contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = {};
    }
  } else {
    data = {
      error:
        res.status === 404
          ? 'auth api not mounted — restart npm start'
          : 'unexpected response from auth api',
    };
  }
  return { ok: res.ok, status: res.status, data };
}

async function getJson(url, { token } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { method: 'GET', headers });
  const contentType = res.headers.get('content-type') || '';
  let data = {};
  if (contentType.includes('application/json')) {
    try {
      data = await res.json();
    } catch {
      data = {};
    }
  } else {
    data = { error: 'unexpected response from auth api' };
  }
  return { ok: res.ok, status: res.status, data };
}

function accessTokenOrThrow() {
  const stored = readStoredSession();
  if (!stored?.accessToken) return null;
  return stored.accessToken;
}

export async function signup(email, password) {
  return postJson('/api/auth/signup', {
    email: normalizeEmail(email),
    password,
  });
}

export async function login(email, password) {
  return postJson('/api/auth/login', {
    email: normalizeEmail(email),
    password,
  });
}

export async function verifyOtp(email, code, purpose) {
  return postJson('/api/auth/verify-otp', {
    email: normalizeEmail(email),
    code: String(code || '').trim(),
    purpose,
  });
}

export async function forgotPassword(email) {
  return postJson('/api/auth/forgot-password', {
    email: normalizeEmail(email),
  });
}

export async function resetPassword(email, code, password) {
  return postJson('/api/auth/reset-password', {
    email: normalizeEmail(email),
    code: String(code || '').trim(),
    password,
  });
}

export async function listAllowlist() {
  const token = accessTokenOrThrow();
  if (!token) {
    return { ok: false, status: 401, data: { error: 'Sign in required.', code: 'AUTH_REQUIRED' } };
  }
  return getJson('/api/auth/allowlist', { token });
}

export async function addAllowlist(email, notes) {
  const token = accessTokenOrThrow();
  if (!token) {
    return { ok: false, status: 401, data: { error: 'Sign in required.', code: 'AUTH_REQUIRED' } };
  }
  return postJson(
    '/api/auth/allowlist',
    { email: normalizeEmail(email), notes: notes || undefined },
    { token }
  );
}

export async function sendInvite(email, notes) {
  const token = accessTokenOrThrow();
  if (!token) {
    return { ok: false, status: 401, data: { error: 'Sign in required.', code: 'AUTH_REQUIRED' } };
  }
  return postJson(
    '/api/auth/invites',
    { email: normalizeEmail(email), notes: notes || undefined },
    { token }
  );
}

export async function revokeAccess(email) {
  const token = accessTokenOrThrow();
  if (!token) {
    return { ok: false, status: 401, data: { error: 'Sign in required.', code: 'AUTH_REQUIRED' } };
  }
  return postJson(
    '/api/auth/revoke',
    { email: normalizeEmail(email) },
    { token }
  );
}

export async function listUsers() {
  const token = accessTokenOrThrow();
  if (!token) {
    return { ok: false, status: 401, data: { error: 'Sign in required.', code: 'AUTH_REQUIRED' } };
  }
  return getJson('/api/auth/users', { token });
}

export async function setUserRole(email, role) {
  const token = accessTokenOrThrow();
  if (!token) {
    return { ok: false, status: 401, data: { error: 'Sign in required.', code: 'AUTH_REQUIRED' } };
  }
  return postJson(
    '/api/auth/users',
    { email: normalizeEmail(email), role },
    { token }
  );
}

export async function refreshSession(refreshToken) {
  return postJson('/api/auth/refresh', { refreshToken });
}

export async function logoutSession(refreshToken, email) {
  return postJson('/api/auth/logout', {
    refreshToken: refreshToken || undefined,
    email: email || undefined,
  });
}

export async function validateAuthSession(accessToken) {
  return postJson('/api/auth/session', { accessToken });
}

/**
 * Validate access JWT; on failure try refresh once.
 */
export async function bootstrapAuthSession() {
  let stored = readStoredSession();
  if (!stored) return null;

  let result = await validateAuthSession(stored.accessToken);
  if (!result.ok && stored.refreshToken) {
    const refreshed = await refreshSession(stored.refreshToken);
    if (refreshed.ok) {
      writeStoredSession({
        accessToken: refreshed.data.accessToken,
        refreshToken: refreshed.data.refreshToken,
        email: refreshed.data.email || stored.email,
        expiresAt: refreshed.data.expiresAt,
      });
      stored = readStoredSession();
      return {
        email: stored?.email || refreshed.data.email,
        expiresAt: stored?.expiresAt || refreshed.data.expiresAt,
        role: refreshed.data.role || 'member',
      };
    }
  }

  if (!result.ok) {
    clearStoredSession();
    return null;
  }

  writeStoredSession({
    accessToken: stored.accessToken,
    refreshToken: stored.refreshToken,
    email: result.data.email || stored.email,
    expiresAt: result.data.expiresAt || stored.expiresAt,
  });

  return {
    email: result.data.email || stored.email,
    expiresAt: result.data.expiresAt || stored.expiresAt,
    role: result.data.role || 'member',
  };
}

/** Parse invite/reset deep-link query from location.search */
export function parseAuthDeepLink(search = '') {
  try {
    const params = new URLSearchParams(search);
    const auth = String(params.get('auth') || '').toLowerCase();
    if (auth !== 'invite' && auth !== 'reset') return null;
    const email = normalizeEmail(params.get('email'));
    const code = String(params.get('code') || '').trim();
    if (!isValidEmailFormat(email)) return null;
    return { auth, email, code: /^\d{6}$/.test(code) ? code : '' };
  } catch {
    return null;
  }
}

export function clearAuthDeepLink() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('auth')) return;
    url.searchParams.delete('auth');
    url.searchParams.delete('email');
    url.searchParams.delete('code');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* ignore */
  }
}
